/**
 * /wireline-slickline/jobs — the tenant's wireline and slickline jobs
 * (GET /wireline-slickline/jobs, fieldiq-api#1338, WL-2).
 *
 * Full-width register; a row opens the job on its own page. The status chips
 * filter on the API (`status=`), so a chip's list is the API's answer, not a
 * client filter. The WLJ- number, status, customer and well come from the job
 * as the API shaped it; the well's identifier is labelled by country (meta).
 *
 * The band counts are a tally of the rows this list returned (no endpoint
 * returns one); a failed read shows `—`, never 0.
 *
 * New Job is WL_DISPATCH (admin, office_staff, supervisor, dispatcher).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, ClipboardList, Plus, Search, Wrench } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWlMeta } from '../../hooks/useWirelineSlickline';
import { useWlJobs } from '../../hooks/useWlJobs';
import { queryLoadFailure } from '../../lib/load-failure';
import { wlWellIdOf } from '../../lib/wireline-slickline';
import { canDispatchWl, wlDay, wlJobStatusSkin } from '../../lib/wireline-slickline-jobs';
import type { WlJob } from '../../types/wireline-slickline-jobs';
import { useOpenRecord } from '../downhole/recordPage';
import { WL_EYEBROW, WlBadge, WlChip, WlChipRow, WlEmpty, WlPanel, WlRegister, type WlColumn } from './WlBits';

const FALLBACK_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const wellIdOf = (j: WlJob, country: string | null | undefined) =>
  wlWellIdOf({ uwi: j.wellUwi, apiNumber: j.wellApiNumber }, { country: country ?? null });

const dates = (j: WlJob) =>
  j.plannedStartOn || j.plannedEndOn ? `${wlDay(j.plannedStartOn)} – ${wlDay(j.plannedEndOn)}` : '—';

export default function WlJobsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const catalog = useWlMeta().data?.data ?? null;
  const query = useWlJobs({ status: status || undefined, q: q.trim() || undefined });
  const jobs = query.data?.data ?? [];
  const meta = query.data?.meta ?? null;
  const loading = query.isLoading;
  const known = loading || queryLoadFailure(query) ? undefined : jobs;
  const canAdd = canDispatchWl(user?.role);
  const statuses = catalog?.jobStatuses?.length ? catalog.jobStatuses : FALLBACK_STATUSES;

  const columns: WlColumn<WlJob>[] = [
    {
      key: 'job',
      header: 'Job',
      render: (j) => (
        <>
          <p className="font-mono font-extrabold text-white">{j.jobNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{j.scopeLabels?.length ? j.scopeLabels.join(', ') : '—'}</p>
        </>
      ),
    },
    { key: 'status', header: 'Status', render: (j) => <WlBadge skin={wlJobStatusSkin(j.status, j.statusLabel)} value={j.status} /> },
    { key: 'customer', header: 'Customer', render: (j) => j.customerName || '—' },
    {
      key: 'well',
      header: 'Well',
      render: (j) => (
        <>
          <p className="text-sm text-white">{j.wellName || '—'}</p>
          <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{wellIdOf(j, meta?.country) || '—'}</p>
        </>
      ),
    },
    { key: 'dates', header: 'Planned', render: (j) => <span className="text-sm">{dates(j)}</span> },
    { key: 'units', header: 'Units', align: 'right', render: (j) => <span className="font-mono text-sm">{displayValue(j.unitCount)}</span> },
    { key: 'sheets', header: 'Sheets', align: 'right', render: (j) => <span className="font-mono text-sm">{displayValue(j.sheetCount)}</span> },
  ];

  return (
    <div data-testid="wl-jobs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title="Jobs"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/wireline-slickline/jobs/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New job
            </button>
          ) : undefined
        }
        stats={[
          { icon: <Briefcase className="size-4" aria-hidden="true" />, label: 'Jobs', sub: status ? 'This filter' : 'All', tone: 'neutral', loading, value: displayValue(known?.length) },
          {
            icon: <Wrench className="size-4" aria-hidden="true" />,
            label: 'In progress',
            tone: 'accent',
            loading,
            // Counted from the loaded rows, so only meaningful when no status filter narrows them.
            value: displayValue(known && !status ? known.filter((j) => j.status === 'in_progress').length : undefined),
          },
          {
            icon: <ClipboardList className="size-4" aria-hidden="true" />,
            label: 'Scheduled',
            tone: 'neutral',
            loading,
            // Counted from the loaded rows, so only meaningful when no status filter narrows them.
            value: displayValue(known && !status ? known.filter((j) => j.status === 'scheduled').length : undefined),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="space-y-3">
            <WlChipRow label="Job status">
              <WlChip label="All" active={status === ''} onClick={() => setStatus('')} />
              {statuses.map((s) => (
                <WlChip key={s.value} label={s.label} active={status === s.value} onClick={() => setStatus(s.value)} />
              ))}
            </WlChipRow>
            <label className="relative block max-w-xl">
              <span className="sr-only">Search jobs</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
                placeholder="Job number, customer, well, PO or AFE"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </label>
          </div>
          <WlPanel
            eyebrow="Job register"
            title="Jobs"
            testId="wl-job-register"
            footer={!loading && known && jobs.length > 0 ? `${jobs.length} job${jobs.length === 1 ? '' : 's'}` : undefined}
          >
            <WlRegister
              query={query}
              rows={jobs}
              columns={columns}
              rail={(j) => wlJobStatusSkin(j.status).rail}
              onOpen={(j) => openRecord(`/wireline-slickline/jobs/${j.id}`)}
              subject="jobs"
              testId="wl-job"
              minWidth="min-w-[980px]"
              empty={
                <WlEmpty>
                  {status || q ? 'No job matches this filter.' : 'There are no wireline jobs yet.'}
                  {!status && !q && canAdd ? ' Create one with New job.' : ''}
                </WlEmpty>
              }
              card={(j) => (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-mono text-sm font-extrabold text-white">{j.jobNumber || '—'}</p>
                    <WlBadge skin={wlJobStatusSkin(j.status, j.statusLabel)} value={j.status} />
                  </div>
                  <p className="mt-1 text-sm text-white">{j.customerName || '—'}</p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                    {j.wellName || '—'}
                    {wellIdOf(j, meta?.country) ? ` · ${wellIdOf(j, meta?.country)}` : ''}
                  </p>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {dates(j)} · {displayValue(j.sheetCount)} sheet{j.sheetCount === 1 ? '' : 's'}
                  </p>
                </>
              )}
            />
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
